import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table'
import { Checkbox } from '../../../../../components/ui/checkbox'
import { useQuery } from '@tanstack/react-query'
import axios from '../../../../../lib/axios'
import useAuth from '../../../../../hooks/useAuth'
import lottie from 'lottie-web'
import ErrorAnimation from '../../../../../assets/animations/error.json'
import toast from 'react-hot-toast'
import { EditIcon, ShieldBanIcon, ShieldCheckIcon } from 'lucide-react'
import { Button } from '../../../../../components/ui/button'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../redux'
import InactivePopup from '../toolbar-kassa/inactive'
import ActivePopup from '../toolbar-kassa/active'

const headClass = `max-h-[15rem] uppercase `
const cellClass = `flex items-center`

type KassaRowProps = {
    kassa: Kassa
    deviescurrencies: DeviesCurrency[]
    selected_kassas: number[];
    toggleItem?: (kassaid: number) => void;
}

const KassaRow = ({kassa,deviescurrencies, selected_kassas=[], toggleItem=()=>{}}: KassaRowProps) => {
    const { t } = useTranslation()
    const { auth } = useAuth()
    const partners = useSelector((state:RootState)=>state.partners)

    const partner = Array.isArray(partners) ? partners.find(p=>p.relnr === kassa.relnr): undefined

    const [selected_currency, setselected_currency] = useState<number[]>([]);

    async function getKassaDevies() {
        try {
            const { data } = await axios.get(`kassadevies/get`, { params: {
                token: auth.data?.token,
                kassaid: kassa.kassaid
            }})
            if(data.errorcode!==0){ throw new Error(data.message) }
            if(!Array.isArray(data.object)){ return [] }
            const selections = data.object.filter((dc:any)=>dc.kassadeviesactive).map((dc:any)=>parseInt(dc.deviescurrency))
            console.log(kassa.kassaid, data.object.filter((dc:any)=>dc.kassadeviesactive).map((dc:any)=>parseInt(dc.deviescurrency)));
            setselected_currency(selections)
            return data.object
        } catch (error) {
            console.log(error)
            toast.error(t('settings.fc.toast_error_fetch_kassa_devies'), { id:'kassa-devies' })
            return null
        }
    }
    
    useEffect(()=>{
        getKassaDevies()
    }, [])

    async function handleChangeChecked(currency_id: number, checked: boolean) {
        try {
            if(!checked){
                setselected_currency(selected_currency.filter(i=>i!==currency_id))
            } else {
                setselected_currency([...selected_currency, currency_id])
            }

            toast.loading(t('settings.fc.toast_loading_kassa_change'), { id:'kassa-devies' })
            const { data } = await axios.put('kassadevies/modify', {
                token: auth.data?.token,
                kassaid: kassa.kassaid,
                deviescurrencyid: currency_id,
                active: checked
            })
            if(data.errorcode!==0){ throw new Error(data.message) }
            toast.success(t('settings.fc.toast_success_kassa_change'), { id:'kassa-devies' })
            getKassaDevies()
        } catch (err) {
            console.log(err)
            toast.error(t('settings.fc.toast_error_kassa_change'), { id:'kassa-devies' })
        }
    }

    return (
        <TableRow >
            <TableCell>
                <div className='flex items-center justify-center'>
                    <Checkbox onCheckedChange={(v)=>{toggleItem(kassa.kassaid)}} checked={selected_kassas.includes(kassa.kassaid)} />
                </div>
            </TableCell>
            <TableCell className={`${kassa.kassaactive ? '' : 'select-none opacity-50'}`}>
                {partner?.name || kassa.relnr} {kassa.kassaactive ? '' : t('settings.fc.inactive')}
            </TableCell>
            {Array.isArray(deviescurrencies) && deviescurrencies.map((dc,i)=>(
                <TableCell key={`d-currency-${i}`} className={`${kassa.kassaactive ? '' : 'select-none opacity-50'}`}>
                    <div className={cellClass}>
                        <Checkbox disabled={!kassa.kassaactive} onCheckedChange={(v)=>{handleChangeChecked(dc.deviescurrencyid, v as boolean)}} checked={selected_currency.includes(dc.deviescurrencyid)} />
                    </div>
                </TableCell>
            ))}
        </TableRow>
    )
}

const KassaTab = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { auth } = useAuth()
    
    const errorRef = useRef<HTMLDivElement | null>(null)
     
    const [selected_kassas, setselected_kassas] = useState<number[]>([]);

    const { data:kassas, refetch:refetchKassas } = useQuery<Kassa[] | null>({
        queryKey: ['kassas'],
        queryFn: async () => {
            try {
                const { data } = await axios.get('/kassa/get', { params: {
                    token: auth.data?.token,
                    cn: "N",
                }})
                if(data.errorcode!==0){ throw new Error(data.message) }
                return data.object
            } catch (error) {
                console.log(error)
                if(errorRef.current){
                    lottie.loadAnimation({
                        container: errorRef.current,
                        renderer: 'svg',
                        loop: true,
                        autoplay: true,
                        animationData: ErrorAnimation,
                    })
                }
                toast.error(t('settings.fc.toast_error_fetch_kassa'), { id:'kassas' })
                return null
            }
        },
        refetchOnWindowFocus: false
    })


    const { data:deviescurrencies, isLoading:isLoadingDC, refetch:refetchDC } = useQuery<DeviesCurrency[] | null>({
        queryKey: ['devies-currency'],
        queryFn: async () => {
            try {
                const { data } = await axios.get('/deviescurrency/get', { params: {
                    token: auth.data?.token
                }})
                if(data.errorcode!==0){ throw new Error(data.message) }
                return data.object
            } catch (error) {
                console.log(error)
                if(errorRef.current){
                    lottie.loadAnimation({
                        container: errorRef.current,
                        renderer: 'svg',
                        loop: true,
                        autoplay: true,
                        animationData: ErrorAnimation,
                    })
                }
                toast.error(t('settings.fc.toast_error_fetch_foreign'), { id:'devies-currency' })
                return null
            }
        },
        refetchOnWindowFocus: false
    })

    // toggle all kassas
    const toggleAll = () => {
        if(!Array.isArray(kassas)){ return }

        // get all ids from group
        const all_ids = kassas.map(k=>k.kassaid)
        
        if(all_ids.length === selected_kassas.length){
            // Remove all current_selected from selected
            setselected_kassas([])
        }
        else {
            // Add all ids to selected
            setselected_kassas([...selected_kassas].concat(all_ids))
        }
    }

     // toggle specific kassa
    const toggleItem = (kassa_id: number) => {
        if(selected_kassas.includes(kassa_id)){
            // Remove current_selected from selected
            setselected_kassas(selected_kassas.filter(i=>i!==kassa_id))
        }
        else {
            // Add current_selected to selected
            setselected_kassas([...selected_kassas, kassa_id])
        }
    }

  return (
    <>
    <div className='sticky z-10 flex items-center p-2 mt-4 bg-white border rounded-sm shadow-sm -top-4 md:-top-8 md:mt-8'>
        <div className='flex gap-1'>
            {/* <Button onClick={()=>{navigate(`edit-kassa/${selected_kassas[0]}`)}} disabled={selected_kassas.length !== 1} size="sm" variant='ghost' className='flex items-center gap-2 bg-white whitespace-nowrap'><EditIcon strokeWidth={1} className='w-5 text-orange-600'/>Wijzigen</Button> */}
            <InactivePopup 
                selected_ids={selected_kassas} 
                trigger={<Button disabled={selected_kassas.length < 1} size="sm" variant='ghost' className='flex items-center gap-2 bg-white whitespace-nowrap'><ShieldBanIcon strokeWidth={1} className='w-5 text-red-600'/>{t('settings.fc.make_inactive')}</Button>}
                onSuccess={()=>{refetchKassas()}}
            />
            <ActivePopup 
                selected_ids={selected_kassas}
                trigger={<Button disabled={selected_kassas.length < 1} size="sm" variant='ghost' className='flex items-center gap-2 bg-white whitespace-nowrap'><ShieldCheckIcon strokeWidth={1} className='w-5 text-green-600'/>{t('settings.fc.make_active')}</Button>}
                onSuccess={()=>{refetchKassas()}}
            />
        </div>
    </div>
    
    {Array.isArray(kassas) && kassas.length > 0 &&
    <div className='mt-4 overflow-auto border rounded-sm md:mt-8'>
        <Table>
            <TableHeader className='relative bg-slate-100'>
                <TableRow>
                    <TableHead className='w-10'><div className='flex items-center justify-center'><Checkbox checked={selected_kassas.length === kassas?.length} onCheckedChange={(v)=>{toggleAll()}} /></div></TableHead>
                    <TableHead className={headClass}><div>{t('settings.fc.kassa')}</div></TableHead>
                    {Array.isArray(deviescurrencies) && deviescurrencies.map((dc,i)=>(
                        <TableHead className={headClass} key={i}><div>{dc.deviescurrencyname}</div></TableHead>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {(Array.isArray(deviescurrencies)) &&
                kassas.map((kassa,i)=>(
                    <KassaRow kassa={kassa} selected_kassas={selected_kassas} toggleItem={(kassaid)=>toggleItem(kassaid)} key={i} deviescurrencies={deviescurrencies} />
                ))}
            </TableBody>
        </Table>
    </div>
    }
    </>
  )
}

export default KassaTab